import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import * as XLSX from 'xlsx';
import { useToast } from '../../hooks/useToast';
import { fetchWorkAreaLabelMap, formatPositionLabel } from './workforcePositionUtils';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateUploadOutlinedSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateFormFieldsSx,
} from '../../../theme/harmonateStyles';

const toIsoDate = (date) => {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, '0');
  const d = `${date.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const enumerateDatesInclusive = (startIso, endIso) => {
  const out = [];
  const cur = new Date(`${startIso}T12:00:00`);
  const end = new Date(`${endIso}T12:00:00`);
  if (cur > end) return out;
  while (cur <= end) {
    out.push(toIsoDate(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
};

const formatDisplayTime = (value) => {
  if (!value || typeof value !== 'string') return '';
  const [h, m] = value.split(':');
  const hour = Number(h);
  if (Number.isNaN(hour) || m == null) return value;
  const normalized = hour % 12 || 12;
  const suffix = hour >= 12 ? 'PM' : 'AM';
  return `${normalized}:${m.slice(0, 2)} ${suffix}`;
};

const formatEmployeeName = (emp) => {
  if (!emp) return '';
  if (emp.fullName && emp.fullName.trim()) return emp.fullName.trim();
  const first = emp.firstName?.trim() || '';
  const last = emp.lastName?.trim() || '';
  const full = `${first} ${last}`.trim();
  return full || emp.employeeCode || String(emp.employeeId || '').slice(0, 8);
};

const shiftLabel = (a) => {
  const st = a.startTime && String(a.startTime).length === 5 ? `${a.startTime}:00` : a.startTime;
  const et = a.endTime && String(a.endTime).length === 5 ? `${a.endTime}:00` : a.endTime;
  return `${formatDisplayTime(String(st || ''))}–${formatDisplayTime(String(et || ''))}`;
};

const defaultRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 29);
  return { from: toIsoDate(start), to: toIsoDate(end) };
};

/**
 * Employee × role × day grid for a date range; Excel export. Opens from Schedule ⋮ menu.
 */
const EmployeeCoverageDialog = ({
  open,
  onClose,
  propertyId,
  refreshKey = 0,
  defaultFromDate,
  defaultToDate,
}) => {
  const { showError } = useToast();
  const tenantId = localStorage.getItem('tenant_id') || '';
  const token = localStorage.getItem('token') || '';
  const backendBase = () => process.env.REACT_APP_BACKEND_URL || '';

  const initial = useMemo(() => defaultRange(), []);
  const [fromDate, setFromDate] = useState(initial.from);
  const [toDate, setToDate] = useState(initial.to);
  const [loading, setLoading] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [employeesById, setEmployeesById] = useState({});
  const [roleNameById, setRoleNameById] = useState({});
  const hasLoadedRef = useRef(false);
  const lastHandledRefreshKeyRef = useRef(0);

  const dateColumns = useMemo(() => enumerateDatesInclusive(fromDate, toDate), [fromDate, toDate]);

  const filteredAssignments = useMemo(() => {
    if (!fromDate || !toDate || fromDate > toDate) return [];
    return (Array.isArray(assignments) ? assignments : []).filter(
      (a) =>
        a.employeeId &&
        a.businessDate &&
        a.businessDate >= fromDate &&
        a.businessDate <= toDate
    );
  }, [assignments, fromDate, toDate]);

  const gridRows = useMemo(() => {
    const keys = new Map();
    filteredAssignments.forEach((a) => {
      const k = `${a.employeeId}|${a.rolePositionId}`;
      if (!keys.has(k)) {
        keys.set(k, { employeeId: a.employeeId, rolePositionId: a.rolePositionId });
      }
    });
    return Array.from(keys.values()).sort((a, b) => {
      const na = formatEmployeeName(employeesById[a.employeeId]);
      const nb = formatEmployeeName(employeesById[b.employeeId]);
      if (na !== nb) return na.localeCompare(nb);
      const ra = roleNameById[a.rolePositionId] || '';
      const rb = roleNameById[b.rolePositionId] || '';
      return ra.localeCompare(rb);
    });
  }, [filteredAssignments, employeesById, roleNameById]);

  const cellText = useCallback(
    (row, dateIso) => {
      const parts = filteredAssignments.filter(
        (a) =>
          a.employeeId === row.employeeId &&
          a.rolePositionId === row.rolePositionId &&
          a.businessDate === dateIso
      );
      if (!parts.length) return '';
      return parts.map((p) => shiftLabel(p)).join('; ');
    },
    [filteredAssignments]
  );

  const loadData = useCallback(async () => {
    if (!tenantId || !propertyId) {
      showError('Select a business unit first.');
      return;
    }
    if (fromDate > toDate) {
      showError('Start date must be on or before end date.');
      return;
    }
    if (dateColumns.length > 120) {
      showError('Range is too large (max 120 days). Narrow the dates.');
      return;
    }
    try {
      setLoading(true);
      const headers = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json',
      };
      const base = backendBase();
      const [assignRes, empRes, roleRes] = await Promise.all([
        fetch(
          `${base}conductor/employee-assignment/by-tenant?tenant_id=${encodeURIComponent(tenantId)}&property_id=${encodeURIComponent(propertyId)}`,
          { method: 'GET', headers }
        ),
        fetch(
          `${base}conductor/employees/by-tenant-and-status?tenant_id=${encodeURIComponent(tenantId)}&status=ACTIVE`,
          { method: 'GET', headers }
        ),
        fetch(
          `${base}conductor/role-positions/by-tenant?tenant_id=${encodeURIComponent(tenantId)}&property_id=${encodeURIComponent(propertyId)}`,
          { method: 'GET', headers }
        ),
      ]);
      if (!assignRes.ok) throw new Error(`Assignments ${assignRes.status}`);
      const assignData = await assignRes.json();
      setAssignments(Array.isArray(assignData) ? assignData : []);

      const empMap = {};
      if (empRes.ok) {
        const empList = await empRes.json();
        (Array.isArray(empList) ? empList : []).forEach((e) => {
          if (e?.employeeId) empMap[e.employeeId] = e;
        });
      }
      setEmployeesById(empMap);

      const roles = {};
      if (roleRes.ok) {
        const roleList = await roleRes.json();
        const workAreaLabels = await fetchWorkAreaLabelMap(
          process.env.REACT_APP_BACKEND_URL,
          {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
          tenantId
        );
        (Array.isArray(roleList) ? roleList : []).forEach((r) => {
          if (r?.rolePositionId) {
            roles[r.rolePositionId] = formatPositionLabel(r, workAreaLabels);
          }
        });
      }
      setRoleNameById(roles);
      hasLoadedRef.current = true;
    } catch (e) {
      showError(e.message || 'Failed to load coverage data');
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId, propertyId, fromDate, toDate, dateColumns.length, token, showError]);

  useEffect(() => {
    if (!open) return;
    if (defaultFromDate) setFromDate(defaultFromDate);
    if (defaultToDate) setToDate(defaultToDate);
  }, [open, defaultFromDate, defaultToDate]);

  useEffect(() => {
    if (!open || !hasLoadedRef.current) return;
    if (refreshKey <= lastHandledRefreshKeyRef.current) return;
    lastHandledRefreshKeyRef.current = refreshKey;
    loadData();
  }, [refreshKey, open, loadData]);

  const exportExcel = () => {
    if (!gridRows.length || !dateColumns.length) {
      showError('Nothing to export. Load data first.');
      return;
    }
    const headerRow = [
      'Employee',
      'Position',
      ...dateColumns.map((d) => {
        const dt = new Date(`${d}T12:00:00`);
        const dayNum = dt.getDate();
        const short = dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        return `${dayNum} (${short})`;
      }),
    ];
    const aoa = [headerRow];
    gridRows.forEach((row) => {
      aoa.push([
        formatEmployeeName(employeesById[row.employeeId]),
        roleNameById[row.rolePositionId] || row.rolePositionId,
        ...dateColumns.map((d) => cellText(row, d)),
      ]);
    });
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Coverage');
    const safeProp = String(propertyId).replace(/[^a-zA-Z0-9-_]/g, '').slice(0, 8) || 'property';
    XLSX.writeFile(wb, `employee-coverage-${safeProp}-${fromDate}-to-${toDate}.xlsx`);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth scroll="paper" PaperProps={harmonateDialogPaperProps}>
      <DialogTitle sx={harmonateDialogTitleSx}>Employee Coverage View</DialogTitle>
      <DialogContent dividers sx={{ ...harmonateDialogContentSx, ...harmonateFormFieldsSx }}>
        <Typography variant="body2" sx={{ mb: 1.5, color: ht.text }}>
          Assignments by employee and position across each day in the range. Export matches this grid.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 2 }}>
          <TextField
            label="From"
            type="date"
            size="small"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const r = defaultRange();
              setFromDate(r.from);
              setToDate(r.to);
            }}
            sx={harmonateUploadOutlinedSx}
          >
            Last 30 days
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={loadData}
            disabled={loading || !propertyId}
            sx={harmonateProceedButtonSx}
          >
            {loading ? <CircularProgress size={18} color="inherit" /> : 'Load'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={exportExcel}
            disabled={!gridRows.length}
            sx={harmonateUploadOutlinedSx}
          >
            Download
          </Button>
        </Box>

        <TableContainer sx={{ maxHeight: 480, border: `1px solid ${ht.line}`, borderRadius: ht.cardRadius, bgcolor: ht.white }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, minWidth: 140, left: 0, zIndex: 3, bgcolor: ht.listBg, color: ht.text }}>
                  Employee
                </TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 120, left: 140, zIndex: 3, bgcolor: ht.listBg, color: ht.text }}>
                  Position
                </TableCell>
                {dateColumns.map((d) => {
                  const dt = new Date(`${d}T12:00:00`);
                  const dayNum = dt.getDate();
                  const dow = dt.toLocaleDateString('en-US', { weekday: 'short' });
                  return (
                    <TableCell key={d} align="center" sx={{ fontWeight: 700, minWidth: 72, whiteSpace: 'nowrap' }}>
                      <Typography variant="caption" display="block" sx={{ fontWeight: 700 }}>
                        {dayNum}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {dow}
                      </Typography>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {!gridRows.length && !loading ? (
                <TableRow>
                  <TableCell colSpan={2 + Math.max(dateColumns.length, 1)} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {propertyId ? 'Choose dates and click Load.' : 'Select a business unit on the schedule first.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                gridRows.map((row) => (
                  <TableRow key={`${row.employeeId}|${row.rolePositionId}`} hover>
                    <TableCell
                      sx={{
                        position: 'sticky',
                        left: 0,
                        bgcolor: ht.white,
                        zIndex: 1,
                        borderRight: `1px solid ${ht.line}`,
                      }}
                    >
                      {formatEmployeeName(employeesById[row.employeeId])}
                    </TableCell>
                    <TableCell
                      sx={{
                        position: 'sticky',
                        left: 140,
                        bgcolor: ht.white,
                        zIndex: 1,
                        borderRight: `1px solid ${ht.line}`,
                      }}
                    >
                      {roleNameById[row.rolePositionId] || '—'}
                    </TableCell>
                    {dateColumns.map((d) => (
                      <TableCell key={d} align="center" sx={{ fontSize: '0.7rem', verticalAlign: 'top', maxWidth: 100 }}>
                        {cellText(row, d) || '—'}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={harmonateCancelButtonSx}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmployeeCoverageDialog;
